import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from "@nextui-org/react";
import { ZButton } from "@/shared/components/button";
import { Upload, File, Trash, RotateCw } from "lucide-react";
import { useState, useEffect, useRef, useMemo, useContext } from "react";
import type { ChangeEvent } from "react";
import type { UploadedFile, UploadProgress } from "@/shared/types/file-upload";
import ZFileName from "@/shared/components/custom/file-upload/FileName";
import { addNotification } from "@/shared/states/notification";
import { cn } from "@/shared/utils/classname-merger";
import { Spinner } from "@nextui-org/react";
import { ZProgress } from "@/shared/components/progress";
import { 
  getCustomizeControls, 
  getCustomizeControlsActions,
  useCustomizeControlsStore,
  type NistCsfControl as NistCsfControlType
} from "@/modules/agent/states/cusomize-controls";
import { getSignedUrl } from "@/shared/requests/get-signed-url";
import { uploadFileReq } from "@/shared/requests/upload-file";
import type { AxiosProgressEvent } from "axios";
import { useIsDemoActive } from "@/shared/states/user";
import { v4 as uuidv4 } from "uuid";
import { useParams } from "react-router-dom";
import { useReassessmentModeStore } from "@/modules/agent/states";
import { NistCheckboxContext } from "./CustomizeControlsTable";
import { AgentSessionType } from "@/modules/sessions/types";
import { fileErrorTypes } from "@/shared/constants";

interface BulkUploadDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedControls: Set<string>;
  onAfterUpload?: () => void;
}

const BulkUploadDocumentsModal = ({ 
  isOpen, 
  onClose,
  selectedControls,
  onAfterUpload,
}: BulkUploadDocumentsModalProps) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [hasValidationError, setHasValidationError] = useState(false);
  const [hasValidFiles, setHasValidFiles] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalOpenCount = useRef(0);
  const isDemo = useIsDemoActive();
  const agentType = useCustomizeControlsStore().agentType;
  
  // Add AbortController refs to track ongoing operations
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const uploadPromisesRef = useRef<Map<string, Promise<void>>>(new Map());
  
  // Cleanup function to abort all ongoing operations
  const cleanup = () => {
    // Abort all ongoing uploads
    abortControllersRef.current.forEach((controller) => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    });
    abortControllersRef.current.clear();
    uploadPromisesRef.current.clear();
    setUploadProgress({});
  };
  
  // Cleanup on unmount or when modal closes
  useEffect(() => {
    if (!isOpen) {
      cleanup();
    }
    return () => cleanup();
  }, [isOpen]);
  
  // Get current agent ID from URL parameters
  const { id: currentAgentId } = useParams<{ id: string }>();
  
  // Get reassessment mode state
  const { isReassessmentMode, agentId: reassessmentAgentId } = useReassessmentModeStore();
  const { setShowCheckboxes: setShowNistCheckboxes } = useContext(NistCheckboxContext);
  
  // Determine if THIS session is actually in reassessment mode
  const isCurrentSessionInReassessment = isReassessmentMode && currentAgentId === reassessmentAgentId;
  
  // Get all selected controls
  const selectedControlsData = useMemo(() => {
    if (!selectedControls.size) return [];
    
    const controls = getCustomizeControls() as NistCsfControlType[];
    return controls.filter(control => selectedControls.has(control.uid));
  }, [selectedControls]);
  
  // Calculate the total number of existing files across all selected controls
  const totalExistingFiles = useMemo(() => {
    if (!selectedControlsData.length) return 0;
    
    // Find the max number of files in any selected control (not the sum)
    return selectedControlsData.reduce((maxFiles, control) => {
      // Count permanent files
      const permanentFilesCount = control.source_urls?.length || 0;
      
      // Count temporary files in reassessment mode
      const tempFilesCount = isCurrentSessionInReassessment 
        ? (control.new_uploaded_files?.length || 0)
        : 0;
        
      const totalFilesInThisControl = permanentFilesCount + tempFilesCount;
      
      // Return the maximum between current max and this control's count
      return Math.max(maxFiles, totalFilesInThisControl);
    }, 0);
  }, [selectedControlsData, isCurrentSessionInReassessment]);
  
  // Calculate remaining file limit
  const maxFilesTotal = 30; // Total max files allowed per control
  const remainingFileLimit = Math.max(0, maxFilesTotal - totalExistingFiles);
  
  // Create a map of existing file names across all selected controls for easy lookup
  const existingFileNamesMap = useMemo(() => {
    const fileNameMap = new Map<string, Set<string>>(); // filename -> set of control UIDs
    
    for (const control of selectedControlsData) {
      // Check permanent files
      if (control.file_names && control.file_names.length > 0) {
        for (const fileName of control.file_names) {
          if (!fileName) continue;
          
          if (!fileNameMap.has(fileName)) {
            fileNameMap.set(fileName, new Set([control.uid]));
          } else {
            fileNameMap.get(fileName)?.add(control.uid);
          }
        }
      }
      
      // Check temporary files in reassessment mode
      if (isCurrentSessionInReassessment && control.new_uploaded_file_names && control.new_uploaded_file_names.length > 0) {
        for (const fileName of control.new_uploaded_file_names) {
          if (!fileName) continue;
          
          if (!fileNameMap.has(fileName)) {
            fileNameMap.set(fileName, new Set([control.uid]));
          } else {
            fileNameMap.get(fileName)?.add(control.uid);
          }
        }
      }
    }
    
    return fileNameMap;
  }, [selectedControlsData, isCurrentSessionInReassessment]);
  
  // Track files that exist in controls separately
  const [filesExistingInControls, setFilesExistingInControls] = useState<Map<string, number>>(new Map());
  
  // Track initial state for this modal session
  useEffect(() => {
    if (isOpen && modalOpenCount.current === 0) {
      modalOpenCount.current += 1;
      // No longer populating existingFiles here
    } else if (!isOpen) {
      // Reset states when modal closes
      setFiles([]);
      setUploadProgress({});
      modalOpenCount.current = 0;
    }
  }, [isOpen]);
  
  // Check validation status whenever files change
  useEffect(() => {
    const hasErrors = files.some(file => doesFileHasError(file));
    const hasValid = files.some(file => 
      file.status === "success" || 
      file.status === "inprogress" || 
      file.status === "gettingurl"
    );
    
    setHasValidationError(hasErrors);
    setHasValidFiles(hasValid);
  }, [files]);
  
  const isFileTooLarge = (file: File, megaBytes = 20) => {
    const sizeLimit = megaBytes * 1024 * 1024;
    return file.size > sizeLimit;
  };

  const handleFiles = async (uploadedFiles: FileList) => {
    setIsUploading(true);
    const maxFiles = remainingFileLimit;
    let counter = files.filter(f => !fileErrorTypes.has(f.status)).length;
    let shouldShowFileTooLargeNotification = false;
    const largeFileNames: string[] = [];

    const newFiles: UploadedFile[] = [];
    // New Map to track which files exist in how many controls
    const newFilesExistingInControls = new Map<string, number>(filesExistingInControls);
    
    // Filter for allowed file types
    const allowedFileTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "application/json",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "text/csv",
    ];

    const validFilesTypes = Array.from(uploadedFiles).filter(file => allowedFileTypes.includes(file.type));
    
    for (const file of validFilesTypes) {
      let alreadyAdded = false;
      if (counter >= maxFiles) {
        newFiles.push({
          id: uuidv4(),
          file,
          preview: URL.createObjectURL(file),
          status: "too_many_files",
          progressBar: false,
        } as UploadedFile);
        alreadyAdded = true;
      }

      const fileTooLarge = isFileTooLarge(file);
      shouldShowFileTooLargeNotification = shouldShowFileTooLargeNotification || fileTooLarge;
      
      if (fileTooLarge) {
        largeFileNames.push(file.name);
      } else {
        counter = counter + 1;
      }

      if (!alreadyAdded) {
        newFiles.push({
          id: uuidv4(),
          file,
          preview: URL.createObjectURL(file),
          status: fileTooLarge ? "too_large" : "gettingurl",
          progressBar: false,
        } as UploadedFile);
      }
    }

    // Check for duplicate filenames
    const fileName = new Set<string>(files.map((oldFile) => oldFile.file.name));
    for (const file of newFiles) {
      if (fileName.has(file.file.name)) {
        if (file.status !== "too_many_files") {
          file.status = "duplicate_files";
        }
      } else {
        fileName.add(file.file.name);
      }

      // Check if the file already exists in any of the selected controls
      if (existingFileNamesMap.has(file.file.name) && file.status !== "too_many_files" && file.status !== "too_large") {
        const controlsWithFile = existingFileNamesMap.get(file.file.name);
        if (controlsWithFile && controlsWithFile.size > 0) {
          // Instead of modifying the status, keep track in a separate map
          newFilesExistingInControls.set(file.file.name, controlsWithFile.size);
        }
      }
    }

    if (maxFiles && counter > maxFiles) {
      addNotification({
        type: "warn",
        title: "Too many files!",
        message: `You can only upload up to ${maxFiles} more files for the selected controls. Please remove ${counter - maxFiles} files to proceed.`,
      });
    }

    if (shouldShowFileTooLargeNotification) {
      addNotification({
        type: "error",
        title: "File size limit exceeded!",
        message:
          largeFileNames.length > 1
            ? `${largeFileNames.length} files exceed the maximum file size of 20mb. Please remove them to proceed.`
            : `The file ${largeFileNames[0]} exceeds the maximum file size of 20mb. Please remove it to proceed.`,
      });
    }

    const validFilesArray = newFiles.filter(
      (fileObj: UploadedFile) => fileObj.status === "gettingurl"
    );
    const invalidFilesArray = newFiles.filter(
      (fileObj: UploadedFile) => fileObj.status !== "gettingurl"
    );

    // Process valid files (get signed URLs and upload)
    await Promise.all(
      validFilesArray.map(async (fileObj) => {
        const controller = new AbortController();
        abortControllersRef.current.set(fileObj.file.name, controller);
        
        const uploadPromise = (async () => {
          try {
            // Check if already aborted
            if (controller.signal.aborted) return;
            
            const url = await getSignedUrl({
              file_names: [fileObj.file.name],
              ...(isDemo ? { max_age: 120 } : {}),
            });
            
            // Check if aborted after getting signed URL
            if (controller.signal.aborted) return;
            
            fileObj.signedUrl = url[0];
            fileObj.status = "inprogress";
            setFiles((prevFiles) => [
              ...prevFiles,
              {
                ...fileObj,
                status: prevFiles.find(
                  (fil) => fil.file.name === fileObj.file.name
                )
                  ? "duplicate_files" as const
                  : fileObj.status,
              },
            ]);
            setUploadProgress((prevProgress) => ({
              ...prevProgress,
              [fileObj.file.name]: 0,
            }));

            await uploadFile(fileObj, controller);
          } catch (e) {
            // Don't show error if operation was aborted
            if (!controller.signal.aborted) {
              console.error(e);
              setFiles((prevFiles) => [
                ...prevFiles,
                { ...fileObj, status: "failed" as const },
              ]);
              setUploadProgress((prevProgress) => {
                const newProgress = { ...prevProgress };
                delete newProgress[fileObj.file.name];
                return newProgress;
              });
            }
          } finally {
            // Clean up controller reference
            abortControllersRef.current.delete(fileObj.file.name);
            uploadPromisesRef.current.delete(fileObj.file.name);
          }
        })();
        
        uploadPromisesRef.current.set(fileObj.file.name, uploadPromise);
        return uploadPromise;
      })
    );

    // Add invalid files to the state
    setFiles((prev) => [...prev, ...invalidFilesArray]);
    setIsUploading(false);

    // Update the map of files existing in controls
    setFilesExistingInControls(newFilesExistingInControls);
  };

  const uploadFile = async (fileObj: UploadedFile, controller?: AbortController) => {
    const name = fileObj.file.name;
    if (!fileObj.signedUrl) {
      return;
    }
    
    // Use provided controller or create a new one
    const abortController = controller || new AbortController();
    if (!controller) {
      abortControllersRef.current.set(name, abortController);
    }
    
    try {
      // Check if already aborted
      if (abortController.signal.aborted) return;
      
      await uploadFileReq(
        fileObj.signedUrl,
        fileObj.file,
        (progressEvent: AxiosProgressEvent) => {
          // Don't update progress if aborted
          if (abortController.signal.aborted) return;
          
          if (progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress((prevProgress) => ({
              ...prevProgress,
              [name]: progress,
            }));
          }
        },
        abortController.signal
      );
      
      // Don't update state if aborted
      if (abortController.signal.aborted) return;
      
      setUploadProgress((prevProgress) => ({
        ...prevProgress,
        [name]: -1,
      }));
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file.name === name
            ? {
              ...f,
              status:
                f.status === "duplicate_files"
                  ? "duplicate_files"
                  : "success" as const,
            }
            : f
        )
      );
    } catch (error) {
      // Don't handle error if operation was aborted
      if (abortController.signal.aborted) return;
      
      console.error(error);
      setUploadProgress((prevProgress) => ({
        ...prevProgress,
        [name]: 100,
      }));

      setTimeout(() => {
        if (!abortController.signal.aborted) {
          setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.file.name === name ? { ...f, status: "failed" as const } : f
            )
          );
          setUploadProgress((prevProgress) => ({
            ...prevProgress,
            [name]: -1,
          }));
        }
      }, 1000);
    } finally {
      // Clean up controller reference
      if (!controller) { // Only clean up if we created it here
        abortControllersRef.current.delete(name);
        uploadPromisesRef.current.delete(name);
      }
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files) {
      void handleFiles(files);
      e.target.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const { files } = e.dataTransfer;
    void handleFiles(files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add("bg-[#F9FAFB]");
  };

  const handleDragLeave = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove("bg-[#F9FAFB]");
  };

  const handleRemoveFile = (fileId: string) => {
    const fileToRemove = files.find(f => f.id === fileId);
    if (!fileToRemove) return;
    
    // Cancel ongoing upload for this file
    const controller = abortControllersRef.current.get(fileToRemove.file.name);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(fileToRemove.file.name);
      uploadPromisesRef.current.delete(fileToRemove.file.name);
    }

    setFiles((prevFiles) => {
      const filteredFiles = prevFiles.filter(f => f.id !== fileId);
      
      // Track which duplicate files need to be made valid
      let removeDuplicateFileFlag = true;
      const convertedFileNames = new Set<string>();

      let validCount = filteredFiles.filter(f => !fileErrorTypes.has(f.status)).length;

      const fileNameSet = new Set<string>();
      
      // Files that need signed URL after status update
      const filesNeedingSignedUrl: UploadedFile[] = [];
      
      // Convert duplicate files to valid if the original is deleted
      // First pass - handle duplicates (DON'T promote too_many_files here)
      const processedFiles = filteredFiles.map(file => {
        let status = file.status;
        
        if (isFileTooLarge(file.file)) {
          status = "too_large" as const;
        } else if (
          file.status === "duplicate_files" &&
          fileToRemove.file.name === file.file.name &&
          !convertedFileNames.has(file.file.name) &&
          removeDuplicateFileFlag
        ) {
          status = "success" as const;
          convertedFileNames.add(file.file.name);
          removeDuplicateFileFlag = false;
          validCount++;
        }
        // NOTE: Don't promote too_many_files here - do it in second pass with proper slot calculation

        if (fileNameSet.has(file.file.name)) {
          if (status !== "too_large" && status !== "too_many_files" && status !== "success") {
            status = "duplicate_files" as const;
          }
        } else if (status !== "too_large" && status !== "too_many_files" && status !== "duplicate_files") {
          fileNameSet.add(file.file.name);
        }
        
        // If now valid and missing signedUrl, mark for upload
        if (
          status === "success" &&
          !file.signedUrl &&
          !["too_large", "too_many_files", "duplicate_files", "failed"].includes(status)
        ) {
          const updatedStatus = "gettingurl" as const;
          filesNeedingSignedUrl.push({ ...file, status: updatedStatus });
          status = updatedStatus;
        }

        return { ...file, status };
      });
      
      // Recalculate available slots
      const currentValidCount = processedFiles.filter(f => 
        f.status === "success" || 
        f.status === "inprogress" || 
        f.status === "gettingurl" || 
        f.status === "retrying"
      ).length;
      
      // Convert "too_many_files" to "success" if there's room
      // BUT never convert files that are too large
      let updatedAvailableSlots = Math.max(0, remainingFileLimit - currentValidCount);
      
      const finalFiles = processedFiles.map(file => {
        // If this is an excess file and we have slots available, mark it as success
        // BUT never promote files that are too large
        if (
          file.status === "too_many_files" && 
          updatedAvailableSlots > 0
        ) {
          // Check if file is too large before promoting
          if (isFileTooLarge(file.file)) {
            return { ...file, status: "too_large" as const };
          } else {
            updatedAvailableSlots--;
            if (!file.signedUrl) {
              const updatedFile = { ...file, status: "gettingurl" as const };
              filesNeedingSignedUrl.push(updatedFile);
              return updatedFile;
            } else {
              return { ...file, status: "success" as const };
            }
          }
        }
        return file;
      });
      
      // After state update, trigger upload for files needing signed URL
      setTimeout(() => {
        filesNeedingSignedUrl.forEach(async (fileObj) => {
          const controller = new AbortController();
          abortControllersRef.current.set(fileObj.file.name, controller);
          
          const uploadPromise = (async () => {
            try {
              setIsUploading(true);
              if (controller.signal.aborted) return;
              
              const url = await getSignedUrl({
                file_names: [fileObj.file.name],
                ...(isDemo ? { max_age: 120 } : {}),
              });
              
              if (controller.signal.aborted) return;
              
              fileObj.signedUrl = url[0];
              fileObj.status = "inprogress";
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === fileObj.id ? { ...fileObj } : f
                )
              );
              setUploadProgress((prev) => ({
                ...prev,
                [fileObj.file.name]: 0,
              }));
              
              await uploadFile(fileObj, controller);
              if (!controller.signal.aborted) {
                setIsUploading(false);
              }
            } catch (e) {
              if (!controller.signal.aborted) {
                console.error(e);
                setFiles((prev) =>
                  prev.map((f) =>
                    f.id === fileObj.id
                      ? { ...fileObj, status: "failed" as const }
                      : f
                  )
                );
                setUploadProgress((prev) => {
                  const newProgress = { ...prev };
                  delete newProgress[fileObj.file.name];
                  return newProgress;
                });
                setIsUploading(false);
              }
            } finally {
              abortControllersRef.current.delete(fileObj.file.name);
              uploadPromisesRef.current.delete(fileObj.file.name);
            }
          })();
          
          uploadPromisesRef.current.set(fileObj.file.name, uploadPromise);
        });
      }, 0);
      
      // Check for any validation errors in the final list
      const hasErrors = finalFiles.some(file => fileErrorTypes.has(file.status) || filesExistingInControls.has(file.file.name));
      setHasValidationError(hasErrors);
      
      // Check if there are any valid files
      const hasValid = finalFiles.some(file => 
        file.status === "success" || 
        file.status === "inprogress" || 
        file.status === "gettingurl"
      );
      setHasValidFiles(hasValid);
      
      return finalFiles;
    });
  };

  const handleRetry = async (fileObj: UploadedFile) => {
    // Cancel any existing upload for this file
    const existingController = abortControllersRef.current.get(fileObj.file.name);
    if (existingController) {
      existingController.abort();
    }
    
    const controller = new AbortController();
    abortControllersRef.current.set(fileObj.file.name, controller);
    
    const retryPromise = (async () => {
      try {
        setIsUploading(true);
        fileObj.status = "retrying";
        setFiles((prevFiles) => [
          ...prevFiles.map((f) => (f.id === fileObj.id ? { ...fileObj } : f)),
        ]);
        
        if (controller.signal.aborted) return;
        
        const url = await getSignedUrl({
          file_names: [fileObj.file.name],
          ...(isDemo ? { max_age: 120 } : {}),
        });
        
        if (controller.signal.aborted) return;
        
        fileObj.signedUrl = url[0];
        fileObj.status = "inprogress";
        setFiles((prevFiles) => [
          ...prevFiles.map((f) => (f.id === fileObj.id ? { ...fileObj } : f)),
        ]);
        setUploadProgress((prevProgress) => ({
          ...prevProgress,
          [fileObj.file.name]: 0,
        }));

        await uploadFile(fileObj, controller);
        if (!controller.signal.aborted) {
          setIsUploading(false);
        }
      } catch (e) {
        if (!controller.signal.aborted) {
          console.error(e);
          setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.id === fileObj.id ? { ...fileObj, status: "failed" as const } : f
            )
          );
          addNotification({
            message: "Error in uploading the file",
            type: "error",
          });
          setUploadProgress((prevProgress) => {
            const newProgress = { ...prevProgress };
            delete newProgress[fileObj.file.name];
            return newProgress;
          });
          setIsUploading(false);
        }
      } finally {
        abortControllersRef.current.delete(fileObj.file.name);
        uploadPromisesRef.current.delete(fileObj.file.name);
      }
    })();
    
    uploadPromisesRef.current.set(fileObj.file.name, retryPromise);
  };

  const handleUpload = () => {
    if (files.length === 0) {
      addNotification({
        type: "error",
        title: "No files selected",
        message: "Please select at least one file to upload",
      });
      return;
    }

    // Extract information from successfully uploaded files
    // Only consider files as successful if they're not duplicates or existing in other controls
    const successfulFiles = files.filter(file => 
      file.status === "success" && !filesExistingInControls.has(file.file.name)
    );
    const newUrls = successfulFiles.map(file => file.signedUrl || '').filter(url => url);
    const newFileNames = successfulFiles.map(file => file.file.name || '').filter(name => name);
    
    // For bulk upload, we directly use the newly uploaded files without checking against prior existing files
    // on individual controls for this modal's purpose. The backend or further logic might handle overall deduplication if needed.
    const uniqueNewUrls = newUrls;
    const uniqueNewFileNames = newFileNames;

    // Update all selected controls with the new files
    getCustomizeControlsActions().setDataState((prevControls) => {
      return prevControls.map(control => {
        // Only update selected controls
        if (selectedControls.has(control.uid)) {
          const nistControl = control as NistCsfControlType;
          
          // Handle differently based on whether we're in reassessment mode
          if (isCurrentSessionInReassessment) {
            // In reassessment mode, add to new_uploaded_files (temp files)
            // Get existing temporary URLs and filenames for this control
            const controlTempUrls = [...(nistControl.new_uploaded_files || [])];
            const controlTempFileNames = [...(nistControl.new_uploaded_file_names || [])];
            
            // Add unique new URLs and filenames to temporary files
            for (let i = 0; i < uniqueNewUrls.length; i++) {
              controlTempUrls.push(uniqueNewUrls[i]);
              controlTempFileNames.push(uniqueNewFileNames[i]);
            }
            
            // Return updated control with new temporary files
            return {
              ...control,
              new_uploaded_files: controlTempUrls,
              new_uploaded_file_names: controlTempFileNames
            };
          }
          
          // In normal mode, add to source_urls (permanent files)
          // Get existing URLs and filenames for this control
          const controlSourceUrls = [...(nistControl.source_urls || [])];
          const controlFileNames = [...(nistControl.file_names || [])];
          
          // Add unique new URLs and filenames
          for (let i = 0; i < uniqueNewUrls.length; i++) {
            controlSourceUrls.push(uniqueNewUrls[i]);
            controlFileNames.push(uniqueNewFileNames[i]);
          }
          
          // Return updated control with new permanent files
          return {
            ...control,
            source_urls: controlSourceUrls,
            file_names: controlFileNames
          };
        }
        
        // Return control unchanged if not selected
        return control;
      });
    });
    
    // Only trigger autosave when NOT in reassessment mode
    if (!isCurrentSessionInReassessment) {
      // Trigger autosave by incrementing the changesMade counter
      getCustomizeControlsActions().setChangesMade((prev) => prev + 1);
    } else {
      console.log("Skipping autosave in reassessment mode");
    }

    if(agentType === AgentSessionType.SOC2_GAP_TYPE1 ||  agentType === AgentSessionType.SOC2_GAP_TYPE2){
      addNotification({
        type: "success",
        title: "Upload Successful",
        message: `${newUrls.length} documents successfully uploaded across ${selectedControls.size} controls.`,
      })
    }

    // Use the onAfterUpload callback if provided, otherwise just close
    if (onAfterUpload) {
      // If in reassessment mode, don't trigger any changes that might lead to autosave
      if (isCurrentSessionInReassessment) {
        // Just close the modal without additional callbacks that might trigger autosave
        setShowNistCheckboxes(false);
        handleModalClose();
        
      } else {
        // In normal mode, use the provided callback
        onAfterUpload();
      }
    } else {
      handleModalClose();
    }
    
    // Reset files array
    setFiles([]);
    // reset selected controls
    selectedControls.clear();
  };

  const doesFileHasError = (fileObj: UploadedFile) => {
    const fileExists = filesExistingInControls.has(fileObj.file.name);
    return (
      fileObj.status === "failed" ||
      fileObj.status === "too_large" ||
      fileObj.status === "too_many_files" ||
      fileObj.status === "duplicate_files" ||
      fileExists
    );
  };

  const getFileNameSuffix = (fileObj: UploadedFile) => {
    let fileNameSuffix = "";

    // Check if the file exists in controls first
    if (filesExistingInControls.has(fileObj.file.name)) {
      fileNameSuffix = " (file exists in 1 or more controls)";
      return fileNameSuffix;
    }

    // Otherwise check other statuses
    switch (fileObj.status) {
      case "too_large":
        fileNameSuffix = " (too large)";
        break;
      case "too_many_files":
        fileNameSuffix = " (Excess file. Remove any.)";
        break;
      case "duplicate_files":
        fileNameSuffix = " (duplicate file)";
        break;
      default:
        break;
    }

    return fileNameSuffix;
  };

  // Render a file item UI to match the FileUpload component
  const renderFileItem = (fileObj: UploadedFile, index: number) => {
    // Check if this file exists in other controls for styling
    const fileExistsInControls = filesExistingInControls.has(fileObj.file.name);
    
    return (
      <div key={index} className="group">
        <div className="flex items-center justify-between gap-2 mt-0">
          <File 
            size={16}
            className="ml-3 min-w-4"
            color={
              fileObj.status === "success" && !fileExistsInControls
                ? "#22c55e"
                : fileObj.status === "inprogress" ? "#000" : "#e7000b"
            }
          />
          <div className="flex-1 flex flex-col justify-center">
            <div className="flex items-center gap-1">
              <p
                className={cn(
                  "text-sm max-w-full",
                  fileObj.status === "success" && !fileExistsInControls
                    ? "text-green-600"
                    : doesFileHasError(fileObj) || fileExistsInControls
                      ? "text-red-600"
                      : "text-black"
                )}
              >
                <ZFileName filename={fileObj.file.name} />
              </p>
              <p className={cn(
                "text-sm max-w-full whitespace-nowrap",
                fileObj.status === "success" && !fileExistsInControls
                  ? "text-green-600"
                  : doesFileHasError(fileObj) || fileExistsInControls
                    ? "text-red-600"
                    : "text-black"
              )}>
                {getFileNameSuffix(fileObj)}
              </p>
              {fileObj.status === "gettingurl" ? <Spinner size="sm" /> : null}
            </div>
            <div>
              {fileObj.status !== "success" &&
                Number.isFinite(uploadProgress[fileObj.file.name]) &&
                uploadProgress[fileObj.file.name] !== -1 ? (
                <ZProgress
                  aria-label="file-upload-progress"
                  value={uploadProgress[fileObj.file.name] || 0}
                  maxValue={100}
                  className={cn("h-[3px] mt-1 [&>*]:" + "bg-blue-600")}
                />
              ) : (
                <></>
              )}
            </div>
          </div>
          {fileObj.status === "failed" || fileObj.status === "retrying" ? (
            <ZButton
              isIconOnly
              size="sm"
              className="bg-transparent opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => void handleRetry(fileObj)}
              isLoading={fileObj.status === "retrying"}
            >
              <RotateCw size={16} color="gray" />
            </ZButton>
          ) : null}
          <ZButton
            isIconOnly
            size="sm"
            className="bg-transparent opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => handleRemoveFile(fileObj.id)}
          >
            <Trash size={16} color="gray" />
          </ZButton>
        </div>
      </div>
    );
  };

  // Update all files section to display error files first
  const renderSortedFiles = () => {
    // Error files first
    const errorFiles = files.filter(file => fileErrorTypes.has(file.status));
    
    // Then valid files
    const validFiles = files.filter(file => !fileErrorTypes.has(file.status));
    
    // Return sorted files
    return (
      <>
        {/* Show newly uploaded files first */}
        {[...errorFiles, ...validFiles].map((file, index) => renderFileItem(file, index))}
      </>
    );
  };

  // Determine if the save button should be enabled
  const isSaveButtonEnabled = () => {
    // Check if there are any successfully uploaded files that don't exist in other controls
    const hasAnyValidFiles = files.some(file => 
      file.status === "success" && !filesExistingInControls.has(file.file.name)
    );
    
    // Disable during upload, if there are errors, or if there are no valid files
    return hasAnyValidFiles && !hasValidationError && !isUploading;
  };

  // Handle modal close with cleanup
  const handleModalClose = () => {
    cleanup();
    onClose();
  };
  
  // Add method to cancel all uploads (can be exposed via ref if needed)
  const cancelAllUploads = () => {
    cleanup();
    // Update file statuses to cancelled
    setFiles((prevFiles) =>
      prevFiles.map((file) => ({
        ...file,
        status: file.status === "inprogress" || file.status === "gettingurl" 
          ? "failed" as const 
          : file.status,
      }))
    );
  };
  
  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} size="lg" style={{height: '84vh'}}>
      <ModalContent>
        <ModalHeader className="flex flex-col pb-2 px-6">
          <div className="text-center mb-3">
            <span className="text-lg font-normal">Bulk Upload Documents</span>
          </div>
          
          <div className="text-sm text-gray-500 text-center">
            {`${selectedControls.size} control${selectedControls.size !== 1 ? 's':''} selected`}
          </div>
        </ModalHeader>
        
        <ModalBody className="pt-4" style={{ overflow: 'hidden' }}>
          {/* Drag and drop upload section */}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className="bg-[#F7F7FA] p-2 flex items-center flex-col justify-center h-32 w-full border-dashed border-[1px] border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 text-left"
            aria-label="Upload files"
          >
            <Upload size={24} />
            <div className="cursor-pointer text-sm mt-4">
              Drag and drop files here or{" "}
              <span className="text-[#006FEE]">browse your computer</span>
            </div>
            <p className="text-xs text-center text-gray-500 mt-2 max-w-80">
              (Accepted files: Up to {Math.max(0, 30 - (totalExistingFiles + files.filter(f => 
                f.status === "success" || f.status === "inprogress" || f.status === "gettingurl" || f.status === "retrying"
              ).length))} files of Excel, JSON, PDF, Word, PNG, JPEG, CSV of up to 20mb)
            </p>
            
            <input
              type="file"
              ref={inputRef}
              multiple={true}
              onChange={handleFileChange}
              className="hidden"
              id="file-input"
              accept={[
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/vnd.ms-excel",
                "application/json",
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "image/png",
                "image/jpeg",
                "image/jpg",
                "text/csv",
              ].join(",")}
            />
          </button>

          {/* All files section - both newly uploaded and existing */}
          <div className="mt-1 flex-grow overflow-y-auto">
            {renderSortedFiles()}
          </div>
        </ModalBody>

        <ModalFooter className="pt-4">
          <Button
            color="primary"
            size="sm"
            radius="sm"
            className="w-[80px]"
            onClick={handleUpload}
            isLoading={isUploading}
            isDisabled={!isSaveButtonEnabled()}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkUploadDocumentsModal; 